import { pointTime, spanText } from '../../shared/lib/format';
import type { TDiscTone } from '../../shared/ui/status-disc';
import type { TCustomerSecret, TSourceCustomer } from './api';

export type TClientShape = 'check' | 'cross' | 'clock' | 'minus';

export interface IClientStatus {
  tone: TDiscTone;
  shape: TClientShape;
  label: string;
  headline: string;
  detail?: string;
}

export interface IClientSource {
  name: string;
  /** Some method is enabled — requests are checked at all. */
  checked: boolean;
  /** An enabled method takes a secret, so each client holds their own. False for an IP allowlist alone. */
  secrets: boolean;
  rejects: boolean;
}

export const clientSourceOf = (source: {
  name: string;
  unauthenticated: boolean;
  clientSecrets: boolean;
  onAuthFailure: string;
}): IClientSource => ({
  name: source.name,
  checked: !source.unauthenticated,
  secrets: source.clientSecrets,
  rejects: source.onAuthFailure === 'reject',
});

// Whole hours read at a glance in a list; the page says the minutes.
const roughly = (ms: number): string => {
  const hours = Math.max(1, Math.round(ms / 3_600_000));
  return hours < 36 ? `${hours} h` : `${Math.round(hours / 24)} d`;
};

const endsAt = (secret: TCustomerSecret): number =>
  secret.expiresAt ? new Date(secret.expiresAt).getTime() : Number.POSITIVE_INFINITY;

// Every live one: the API and MCP can leave several old ones overlapping, or one active per method.
export const liveSecrets = (secrets: readonly TCustomerSecret[]) => ({
  current: secrets
    .filter((secret) => secret.state === 'active')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
  old: secrets.filter((secret) => secret.state === 'grace').sort((a, b) => endsAt(a) - endsAt(b)),
});

const countWord = (n: number): string => (n === 2 ? 'both' : `all ${n}`);

const lastRequest = (row: TSourceCustomer, now: number, timeZone?: string): string =>
  row.customer.activity.lastRequestAt
    ? ` · last request ${pointTime(row.customer.activity.lastRequestAt, now, timeZone)}`
    : '';

// Expired and revoked secrets verify nothing, so they never count as live.
export const clientStatus = (
  row: TSourceCustomer,
  source: IClientSource,
  now: number,
  timeZone?: string,
): IClientStatus => {
  const live = liveSecrets(row.secrets);
  const current = live.current[0] ?? null;
  // The one that ends last: until then some old secret still works.
  const last = live.old.at(-1) ?? null;
  const refusal = source.rejects
    ? 'every request is rejected'
    : 'every request is delivered marked as failed';

  if (row.customer.state === 'disabled') {
    return { tone: 'suppressed', shape: 'minus', label: 'Disabled', headline: 'Disabled' };
  }
  if (!source.checked) {
    return {
      tone: 'suppressed',
      shape: 'minus',
      label: 'Not checked',
      headline: `Not checked${lastRequest(row, now, timeZone)}`,
      detail: `${source.name} checks nothing yet, so everything sent to this address is accepted as it arrives`,
    };
  }
  if (!source.secrets) {
    return {
      tone: 'suppressed',
      shape: 'minus',
      label: 'No secret needed',
      headline: `No secret needed${lastRequest(row, now, timeZone)}`,
      detail: `${source.name} checks only the IP address a request comes from, the same allowlist for every client`,
    };
  }
  if (current && last) {
    const ends = last.expiresAt ? new Date(last.expiresAt).getTime() - now : null;
    const olds = live.old.length;
    return {
      tone: 'motion',
      shape: 'clock',
      label:
        ends === null
          ? 'Rotating'
          : `Rotating · ${olds === 1 ? 'old ends' : 'old ones end'} in ${roughly(ends)}`,
      headline: `Rotating · ${countWord(live.current.length + olds)} secrets accepted`,
      detail:
        last.expiresAt && ends !== null
          ? `new secret since ${pointTime(current.createdAt, now, timeZone)} · ${olds === 1 ? 'the old one ends' : `${olds} old ones, the last ends`} ${pointTime(last.expiresAt, now, timeZone)}, in ${spanText(Math.max(0, ends))}`
          : undefined,
    };
  }
  if (last) {
    const olds = live.old.length;
    return {
      tone: 'motion',
      shape: 'clock',
      label: olds === 1 ? 'Only the old one, ending' : 'Only old ones, ending',
      headline:
        olds === 1
          ? 'Only the old secret works, and it is ending'
          : `Only ${olds} old secrets work, and they are ending`,
      detail: last.expiresAt
        ? `${olds === 1 ? 'it ends' : 'the last ends'} ${pointTime(last.expiresAt, now, timeZone)}`
        : undefined,
    };
  }
  if (!current) {
    return row.secrets.length === 0
      ? {
          tone: 'suppressed',
          shape: 'minus',
          label: 'No secret yet',
          headline: `No secret yet · ${refusal}`,
        }
      : {
          tone: 'failure',
          shape: 'cross',
          label: 'No working secret',
          headline: `No working secret · ${refusal}`,
          detail: 'every secret they had here has ended or been revoked',
        };
  }
  if (live.current.some((secret) => secret.lastUsedAt)) {
    return {
      tone: 'success',
      shape: 'check',
      label: 'Verified',
      headline: `Verified${lastRequest(row, now, timeZone)}`,
    };
  }
  if (!row.customer.activity.lastRequestAt) {
    return {
      tone: 'suppressed',
      shape: 'minus',
      label: 'Nothing received yet',
      headline: 'Nothing received yet',
    };
  }
  return {
    tone: 'suppressed',
    shape: 'minus',
    label: 'Not matched yet',
    headline: `Their secret has not matched yet${lastRequest(row, now, timeZone)}`,
    detail:
      new Date(row.customer.activity.lastRequestAt).getTime() <
      new Date(current.createdAt).getTime()
        ? 'the last request came before it was added'
        : undefined,
  };
};

export const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};
